import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { SearchTextField } from "@/controls/SearchTextField";
import { ATHENS_GRAY } from "@/shared/colors";
import { Clip } from "@/shared/models/Clip";
import { getClips } from "@/shared/repositories/ClipRepository";
import { ClipsTable } from "./components/ClipsTable";
import {
	ClipModalMode,
	UpsertDeleteModal,
} from "./components/UpsertDeleteModal";

type useClipsProps = {
	onError?: (error: Error) => void;
};

export const useClips = ({ onError }: useClipsProps) => {
	return useQuery<Clip[], Error>({
		queryKey: ["clips"],
		queryFn: () => getClips(),
		onError: onError,
	});
};

export const ClipsPage = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { clipId: clipIdParam } = useParams<{ clipId?: string }>();
	const selectedClipId = clipIdParam ? Number(clipIdParam) : null;

	const [error, setError] = useState("");
	const [quickFilterText, setQuickFilterText] = useState("");
	const [modalMode, setModalMode] = useState<ClipModalMode>(null);
	const [modalClipId, setModalClipId] = useState<number | null>(null);

	const { data: clips, isLoading } = useClips({
		onError: (e) => setError(e.message),
	});

	const handleSelectionChange = useCallback(
		(id: number | null) => {
			if (id === selectedClipId) return;
			if (id == null) {
				navigate("/clips", { replace: true });
			} else {
				navigate(`/clips/${id}`, { replace: true });
			}
		},
		[selectedClipId, navigate],
	);

	const handleOpenEdit = useCallback((clip: Clip) => {
		if (clip.ClipId == null) return;
		setModalClipId(clip.ClipId);
		setModalMode("update");
	}, []);

	const handleOpenDelete = useCallback((clip: Clip) => {
		if (clip.ClipId == null) return;
		setModalClipId(clip.ClipId);
		setModalMode("delete");
	}, []);

	const handleOpenCreate = useCallback(() => {
		setModalClipId(null);
		setModalMode("create");
	}, []);

	const handleModalClose = useCallback(
		(shouldRefresh: boolean, newClipId?: number) => {
			const wasCreate = modalMode === "create";
			setModalMode(null);
			setModalClipId(null);
			if (shouldRefresh) {
				queryClient.invalidateQueries({ queryKey: ["clips"] });
				if (modalClipId != null) {
					queryClient.invalidateQueries({ queryKey: ["clip", modalClipId] });
				}
				if (newClipId != null) {
					queryClient.invalidateQueries({ queryKey: ["clip", newClipId] });
				}
			}
			if (wasCreate && newClipId != null) {
				navigate(`/clips/${newClipId}`);
			}
		},
		[modalMode, modalClipId, queryClient, navigate],
	);

	return (
		<>
			<HelmetDexteel title="Clips Catalog" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				style={{ backgroundColor: ATHENS_GRAY }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Clips Catalog
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 4 }}>
					<Paper
						elevation={1}
						sx={{
							padding: 2,
							height: "100%",
							display: "flex",
							alignItems: "center",
						}}
					>
						<SearchTextField
							value={quickFilterText}
							onChange={(text) => setQuickFilterText(text)}
							label="Search clips"
						/>
					</Paper>
				</Grid2>
				<Grid2 size={{ xs: "grow" }} />

				<Grid2 size={{ md: 12, xs: 12 }}>
					<ClipsTable
						isLoading={isLoading}
						rows={clips || []}
						quickFilterText={quickFilterText}
						selectedClipId={selectedClipId}
						onSelectionChange={handleSelectionChange}
						onOpenEdit={handleOpenEdit}
						onOpenDelete={handleOpenDelete}
						onOpenCreate={handleOpenCreate}
					/>
				</Grid2>
			</Grid2>

			<UpsertDeleteModal
				mode={modalMode}
				clipId={modalClipId}
				onClose={handleModalClose}
			/>

			<ErrorModal
				error={error}
				onHide={() => {
					setError("");
				}}
			/>
		</>
	);
};
